import {
  CUSTOMER_CREDIT_TYPES,
  getCustomerCreditBalance,
} from '@eliadmin/core/src/api/customers/customer-credit-utils';
import { deleteCoupon } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartPaymentsState } from '@eliadmin/core/src/hooks/cart/use-cart-payments-state';
import { useCartShipments } from '@eliadmin/core/src/hooks/cart/use-cart-shipments';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { LuX } from 'react-icons/lu';

export type CartSummaryCardProps = {
  className?: string;
  itemsDisplayType?: 'list' | 'total';
  usePaymentName?: boolean;
  useShipmentName?: boolean;
};

export function CartSummaryCard({
  className,
  itemsDisplayType = 'total',
  usePaymentName = true,
  useShipmentName = true,
}: CartSummaryCardProps) {
  const { data: cart } = useCart();
  const { data: payments } = useCartPaymentsState();
  const { data: shipments } = useCartShipments();
  const form = useFormContext<CreateOrderFormValues>();
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const loyaltyProgram = useGlobalStore(s => s.generalLayout.loyalty_program);
  const me = useCustomerStore(s => s.me);

  const activePaymentId = cart.paymentType?.id;
  const activeShipmentId = cart.shipment?.id;
  const selectedCountry = form.watch('country.id');

  const queryClient = useQueryClient();
  const deleteCouponMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: deleteCoupon,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.CART] });
    },
  });

  const myLoyaltyPoints = getCustomerCreditBalance(
    me?.customer,
    CUSTOMER_CREDIT_TYPES.LOYALTY_POINTS,
  );

  const activePayment = useMemo(() => {
    return payments?.find(payment => String(payment.id) === String(activePaymentId)) ?? null;
  }, [activePaymentId, payments]);

  const activeShipment = useMemo(() => {
    if (isNullish(activeShipmentId)) return null;

    return shipments?.find(shipment => String(shipment.id) === String(activeShipmentId)) ?? null;
  }, [activeShipmentId, shipments]);

  const totalPrice = useMemo(() => {
    const cartTotal = cart.total;

    // if (!activePayment || !activeShipment) return cartTotal;

    // TODO:
    // if (activePayment.price_with_vat) {
    //   return cartTotal + activePayment.price_with_vat;
    // }

    // if (activeShipment.price_with_vat) {
    //   return cartTotal + activeShipment.price_with_vat;
    // }

    return cartTotal;
  }, [cart.total]);

  const totalPriceProductsNoVat = cart.items_total_without_vat;
  const totalPriceNoVat = cart.total_without_vat;

  const priceItems = useMemo(() => {
    const result: { label: string; value: string }[] = [];

    if (itemsDisplayType === 'total') {
      result.push({
        label: t('text351'),
        value: formatPrice(totalPriceProductsNoVat, currency, currencySettings),
      });
    }

    result.push({
      label: `${t('text64')}`,
      value: formatPrice(cart.items_total - totalPriceProductsNoVat, currency, currencySettings),
    });

    if (templateSettings?.product_detail?.price_without_vat_first?.enabled) {
      result.push({
        label: `${t('text351')} (${t('text1122')})`,
        value: formatPrice(cart.items_total, currency, currencySettings),
      });
    }

    if (activePayment) {
      result.push({
        label: usePaymentName ? `${t('text158')}: ${activePayment.name}` : t('text344'),
        // value: formatPrice(roundPrice(activePayment.price), currency),
        value: cart.payment_type_total
          ? formatPrice(cart.payment_type_total, currency, currencySettings)
          : t('text349'),
      });
    }

    if (activeShipment) {
      result.push({
        label: useShipmentName ? `${t('text153')}: ${activeShipment.name}` : t('text345'),
        value: cart.shipment_total
          ? formatPrice(cart.shipment_total, currency, currencySettings)
          : t('text349'),
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePayment,
    activeShipment,
    cart.items_total,
    cart.payment_type_total,
    cart.shipment_total,
    selectedCountry,
    currency,
    currencySettings,
    itemsDisplayType,
    t,
    usePaymentName,
    useShipmentName,
    totalPriceProductsNoVat,
    totalPriceNoVat,
  ]);

  return (
    <Card
      className={cn(
        'ea-cart-summary-card',
        'flex w-full flex-col items-start justify-start gap-2 rounded-[4px] border border-border bg-transparent p-3 sm:p-4 lg:p-6',
        className,
      )}
    >
      <CardContent
        className={cn(
          'flex w-full flex-col items-start justify-start gap-2 p-0',
          'ea-cart-summary-card__content',
        )}
      >
        {/* list items */}
        {/* {itemsDisplayType === 'list' && (
          <div className="flex flex-col gap-2">
            {cart?.items?.map(item => (
              <div className="grid grid-cols-12 gap-2" key={item.id}>
                <span className="col-span-5 line-clamp-1 text-ellipsis text-sm font-bold">
                  {item.name}
                </span>
                <span className="col-span-2 text-right text-sm text-muted-foreground">
                  {item.qty} {item.unit}
                </span>
                <span className="col-span-3 text-right text-sm text-muted-foreground">
                  {formatPrice(item.price, currency, currencySettings)} / {item.unit}
                </span>
                <span className="col-span-2 text-right text-sm">
                  {formatPrice(item.price_subtotal, currency, currencySettings)}
                </span>
              </div>
            ))}
            <Separator />
          </div>
        )} */}
        <div className={cn('flex w-full flex-col gap-2', 'ea-cart-summary-card__total')}>
          {priceItems.map(item => (
            <div
              className={cn(
                'flex w-full flex-row justify-between',
                'ea-cart-summary-card__total-price',
              )}
              key={`${item.label}-${item.value}`}
            >
              <p
                className={cn(
                  'ea-cart-summary-card__total-price-item-label',
                  'text-sm tracking-tight text-[#666666] sm:text-base',
                )}
              >
                {item.label}
              </p>

              <p
                className={cn(
                  'ea-cart-summary-card__total-price-item-value',
                  'text-sm tracking-tight text-[#666666] sm:text-base',
                )}
                aria-label={item.label}
              >
                {item.value}
              </p>
            </div>
          ))}
          {cart.coupons?.map(coupon => (
            <div
              className={cn(
                'flex flex-row items-center justify-between',
                'ea-cart-summary-card__coupon',
              )}
              key={coupon.code}
            >
              <p
                className={cn(
                  'flex flex-row items-center gap-2 text-sm tracking-tight text-[#666666] sm:text-base',
                  'ea-cart-summary-card__coupon-label',
                )}
              >
                {t('text167')}
                <Button
                  size="none"
                  variant="none"
                  className={cn(
                    'text-destructive transition-all hover:opacity-50',
                    'ea-cart-summary-card__coupon-delete',
                  )}
                  aria-label={t('text4054')}
                  isLoading={deleteCouponMutation.isPending}
                  onClick={() =>
                    deleteCouponMutation.mutate({
                      locale,
                      body: { coupon: coupon.code },
                      authToken: me?.authToken,
                    })
                  }
                >
                  <LuX size={16} aria-hidden />
                </Button>
              </p>

              <p
                className={cn(
                  'ea-cart-summary-card__coupon-value',
                  'text-sm tracking-tight text-[#666666] sm:text-base',
                )}
                aria-label={t('text167')}
              >
                -&nbsp;{formatPrice(coupon.discounted, currency, currencySettings)}
              </p>
            </div>
          ))}
          {!!loyaltyProgram && myLoyaltyPoints > 0 && (
            <div
              className={cn(
                'flex flex-row items-center justify-between',
                'ea-cart-summary-card__loyalty-points',
              )}
              key={'loyalty-points'}
            >
              <span
                className={cn(
                  'flex flex-row items-center gap-2 text-sm tracking-tight text-[#666666] sm:text-base',
                  'ea-cart-summary-card__loyalty-points-label',
                )}
              >
                {t('text572')}
              </span>

              <span
                className={cn('text-sm font-bold', 'ea-cart-summary-card__loyalty-points-value')}
                aria-label={t('text572')}
              >
                - {formatPrice(cart.loyalty_program_discount, currency, currencySettings)}{' '}
              </span>
            </div>
          )}
        </div>
        {/* total */}
        <div
          className={cn(
            'flex w-full flex-row items-center justify-between',
            'ea-cart-summary-card__total',
          )}
        >
          <p
            className={cn(
              'h5',
              'font-semibold tracking-tight text-black',
              'ea-cart-summary-card__total-label',
            )}
            id="together-price"
          >
            {templateSettings?.product_detail?.price_without_vat_first?.enabled
              ? `${t('text3406')} (${t('text443')})`
              : t('text3406')}
          </p>
          <strong
            className={cn(
              'h5',
              'font-semibold tracking-tight text-black',
              'ea-cart-summary-card__total-value',
            )}
            aria-label={t('text3406')}
          >
            {formatPrice(totalPrice, currency, currencySettings)}
          </strong>
        </div>
      </CardContent>
    </Card>
  );
}
